import * as React from 'react';
import { observable, action, computed } from 'mobx';
import { observer } from 'mobx-react';
import { Button, Panel, Form, FormControl, FormGroup, Col, ControlLabel, InputGroup } from 'react-bootstrap';
import { bind } from '../../utils/decorators';
import * as ApiTypes from '../../models/ApiTypes';
import { round } from '../../utils/round';
import ObjektMessmittelUebernehmenModal from './ObjektMessmittelUebernehmen';
import ObjektAnlegen, { ObjektAnlegenHeader } from './ObjektAnlegen';
import * as ApiTypesBps from '../../models/ApiTypesBps2021';

/** Formular zum Anlegen eines neuen Bremsprüfstands (Kurz BPS) */
@observer
export default class ObjektAnlegenBPS extends ObjektAnlegen {

  @observable objekt: Partial<ApiTypes.IObjekt> = {
    objektid: '',
    hersteller: '',
    name: '',
    sernr: '',
    typbezeichnung: '',
    typ: 'BPS',
    bemerkung: ''
  };

  @observable objektMeta = {
    typzusatz: '',
    rollendurchmesser: '',
    hebelarmlaenge: '',
    uebersetzungzaehler: '',
    uebersetzungnenner: '',
    baujahr: '',
    inbetriebnahme: '',
    fzart: '',
    anzeigeart: '',
    anzeigeaufloesung: ''
  };

  async componentDidMount() {
    const objektVorlagen = await this.apiStore.objektVorlagen('bps');
    if (objektVorlagen.ok) {
      this.setObjektVorlagen(objektVorlagen.json);
    }
  }

  @bind async handleObjektAnlegenSubmit(event: React.FormEvent<FormControl>) {
    event.preventDefault();

    const objekt = Object.assign({}, this.objekt);
    objekt.meta = [];
    objekt.meta.push({ bezeichnung: 'rollendurchmesser', inhalt: this.objektMeta.rollendurchmesser });
    objekt.meta.push({ bezeichnung: 'hebelarmlänge', inhalt: this.objektMeta.hebelarmlaenge });
    objekt.meta.push({ bezeichnung: 'übersetzungsverhältnis_zähler', inhalt: this.objektMeta.uebersetzungzaehler });
    objekt.meta.push({ bezeichnung: 'übersetzungsverhältnis_nenner', inhalt: this.objektMeta.uebersetzungnenner });
    objekt.meta.push({ bezeichnung: 'baujahr', inhalt: this.objektMeta.baujahr });
    objekt.meta.push({ bezeichnung: 'inbetriebnahme', inhalt: this.objektMeta.inbetriebnahme });
    objekt.meta.push({ bezeichnung: 'fzart', inhalt: this.objektMeta.fzart });
    objekt.meta.push({ bezeichnung: 'anzeigeart', inhalt: this.objektMeta.anzeigeart });
    objekt.meta.push({ bezeichnung: 'anzeigeaufloesung', inhalt: this.objektMeta.anzeigeaufloesung });

    if (this.props.onAddObjekt) {
      this.props.onAddObjekt(objekt);
    }
  }

  @computed get uebersetzungsVerhaeltnis() {
    if (parseInt(this.objektMeta.uebersetzungzaehler, 10) === NaN || parseInt(this.objektMeta.uebersetzungnenner, 10) === NaN || parseInt(this.objektMeta.uebersetzungnenner, 10) === 0) {
      return undefined;
    }
    return round(parseInt(this.objektMeta.uebersetzungzaehler, 10) / parseInt(this.objektMeta.uebersetzungnenner, 10), 5);
  }

  /** Übernimmt die Daten aus der gewählten Vorlage in das anzulegende Objekt */
  @bind @action uebernehmeObjektEigenschaften() {
    const vorlage = this.vorlageZuModell(this.objekt.hersteller, this.objekt.typbezeichnung);
    if (vorlage) {
      this.objekt.art = vorlage.art;
      // Klartextname setzen
      switch (this.objekt.art) {
        case 'rolle':
          this.objekt.name = 'Rollenbremsprüfstand';
          break;
        case 'platte':
          this.objekt.name = 'Plattenbremsprüfstand';
          break;
        default:
          this.objekt.name = '';
      }
      // Eigenschaften aus Objektvorlage übernehmen
      this.objektMeta.rollendurchmesser = this.vorlageMetaDatum(vorlage, 'rollendurchmesser');
      this.objektMeta.hebelarmlaenge = this.vorlageMetaDatum(vorlage, 'hebelarmlänge');
      this.objektMeta.uebersetzungzaehler = this.vorlageMetaDatum(vorlage, 'übersetzungsverhältnis', 'zähler');
      this.objektMeta.uebersetzungnenner = this.vorlageMetaDatum(vorlage, 'übersetzungsverhältnis', 'nenner');
      this.objektMeta.fzart = '';
      this.objektMeta.anzeigeart = (this.objekt.art === ApiTypesBps.BpsArt.Platte) ? 'analog' : '';
      this.objektMeta.anzeigeaufloesung = '';
    } else {
      this.objekt.art = '';
      this.objekt.name = '';
      this.objektMeta.rollendurchmesser = '';
      this.objektMeta.hebelarmlaenge = '';
      this.objektMeta.uebersetzungzaehler = '';
      this.objektMeta.uebersetzungnenner = '';
      this.objektMeta.fzart = '';
      this.objektMeta.anzeigeart = '';
      this.objektMeta.anzeigeaufloesung = '';
    }
  }

  /** Übernimmt die Daten aus einem Messmittel der ÜO, soweit diese im ÜO Bereich vorhanden sind */
  @bind @action uebernehmeMessmittelDaten(messmittel: ApiTypes.IMessmittelUO) {
    this.objekt.objektid = messmittel.pmnummer;
    this.objekt.messmittelkennung = messmittel.pmnummer;
    const vorlageHersteller = this.objektVorlagen.find(el => el.hersteller.toLocaleLowerCase() === messmittel.hersteller.toLocaleLowerCase());
    if (vorlageHersteller) {
      this.objekt.hersteller = vorlageHersteller.hersteller;
    }
    this.objekt.sernr = messmittel.seriennummer;
    // Bei der Übernahme aus einem ÜO Messmittel passt der Modellbezeichner wegen der freien Eingabe u.U. nicht zur Vorlage
    this.objektVorlagenTypen = this.vorlagenZuHersteller(messmittel.hersteller);
    // Zum Messmittel passende Vorlage wählen, wegen freier Eingabemöglichkeit im ÜO Umfeld nicht auf die Schreibweise achten
    const vorlage = this.vorlageZuModell(messmittel.hersteller, messmittel.name);
    if (vorlage) {
      this.objekt.typbezeichnung = vorlage.modell;
      this.uebernehmeObjektEigenschaften();
    }
    // @todo: Nicht vorhandene Vorlage visualisieren
    this.hideModal();
  }

  @bind @action handleEditValueChanged(event: React.FormEvent<FormControl>) {
    const value = (event.target as HTMLInputElement).value;
    const id = (event.target as HTMLInputElement).id;

    if (id in this.objekt) {
      this.objekt[id] = value;
      // Beim Wechsel des Herstellers müssen bereits gewählte abhängige Daten gelöscht werden
      if (id === 'hersteller') {
        this.objekt.typbezeichnung = '';
        this.objekt.art = '';
        this.objektMeta.rollendurchmesser = '';
        this.objektMeta.hebelarmlaenge = '';
        this.objektMeta.uebersetzungzaehler = '';
        this.objektMeta.uebersetzungnenner = '';
        this.objektMeta.fzart = '';
        this.objektMeta.anzeigeart = '';
        this.objektMeta.anzeigeaufloesung = '';
        // Auswählbare Vorlagen auf die für den Hersteller verfügbaren einschränken
        this.objektVorlagenTypen = [];
        this.objektVorlagenTypen = this.objektVorlagen.filter(vorlage => vorlage.hersteller === this.objekt.hersteller);
      }
      // Beim Wechsel des Typs (aka Modell) die in der Objektvorlage hinterlegten Eigenschaften in das Formular übernehmen
      if (id === 'typbezeichnung') {
        this.uebernehmeObjektEigenschaften();
      }
      return;
    }

    if (id in this.objektMeta) {
      this.objektMeta[id] = value;
    }
  }

  validateMeta() {
    const { objekt, objektMeta, uebersetzungsVerhaeltnis } = this;
    // Das Objekt darf sich nur anlegen lassen, wenn die Metadaten entsprechend der Art (Rolle oder Platte) ausgefüllt wurden
    if (objekt.art === ApiTypesBps.BpsArt.Rolle) {
      return (
        (objektMeta.rollendurchmesser.trim() !== '') && (objektMeta.hebelarmlaenge.trim() !== '') && (objektMeta.uebersetzungzaehler.trim() !== '') &&
        (objektMeta.uebersetzungnenner.trim() !== '') && (objektMeta.baujahr.trim() !== '') && (objektMeta.inbetriebnahme.trim() !== '') &&
        (objektMeta.anzeigeart.trim() !== '') && (objektMeta.anzeigeaufloesung !== '') && (objektMeta.fzart !== '') &&
        (uebersetzungsVerhaeltnis !== undefined)
      );
    }
    if (objekt.art === ApiTypesBps.BpsArt.Platte) {
      return (
        (objektMeta.baujahr.trim() !== '') && (objektMeta.inbetriebnahme.trim() !== '') &&
        (objektMeta.anzeigeart.trim() !== '') && (objektMeta.anzeigeaufloesung !== '') &&
        (objektMeta.fzart !== '')
      );
    }
    return true;
  }

  validate() {
    return (this.objekt.name && this.objekt.name.trim() !== '') && this.validateMeta();
  }

  renderEigenschaften() {
    const { objekt, objektMeta } = this;

    // Die Eingabe der Eigenschaften ist abhängig von der Art des Prfüstands, weshalb diese erst nach Auswahl einer gültigen Vorlage angezeigt werden soll

    if ((objekt.art !== ApiTypesBps.BpsArt.Rolle) && (objekt.art !== ApiTypesBps.BpsArt.Platte)) {
      return null;
    }

    return (
          <div>
            {/* Die zu erfassenden Eigenschaften hängen von der Art des Prüfstands ab und werden entsprechend ein- oder ausgeblendet */}

            {this.objekt.art === ApiTypesBps.BpsArt.Rolle && <div>
              {/* Rollendurchmesser */}
              <FormGroup validationState={objektMeta.rollendurchmesser === '' ? 'error' : undefined}  controlId="rollendurchmesser">
                <Col sm={3} componentClass={ControlLabel}>Rollendurchmesser</Col>
                <Col sm={9}>
                  <InputGroup>
                    <FormControl type="number" step="any" label="Rollendurchmesser" id="rollendurchmesser" value={objektMeta.rollendurchmesser} onChange={this.handleEditValueChanged}/>
                    <InputGroup.Addon>mm</InputGroup.Addon>
                  </InputGroup>
                </Col>
              </FormGroup>
              {/* Hebelarmlänge */}
              <FormGroup validationState={objektMeta.hebelarmlaenge === '' ? 'error' : undefined}  controlId="hebelarmlaenge">
                <Col sm={3} componentClass={ControlLabel}>Hebelarmlänge</Col>
                <Col sm={9}>
                  <InputGroup>
                    <FormControl type="number" step="any" label="Hebelarmlänge" id="hebelarmlaenge" value={objektMeta.hebelarmlaenge} onChange={this.handleEditValueChanged}/>
                    <InputGroup.Addon>mm</InputGroup.Addon>
                  </InputGroup>
                </Col>
              </FormGroup>
              {/* Übersetzungsverhältnis */}
              <FormGroup validationState={(objektMeta.uebersetzungzaehler === '' || objektMeta.uebersetzungnenner === '') ? 'error' : undefined}>
                <Col sm={3}componentClass={ControlLabel}>Übersetzung</Col>
                <Col sm={3}>
                  <InputGroup>
                    <InputGroup.Addon>Rolle</InputGroup.Addon>
                    <FormControl type="number" step="1" min="1" id="uebersetzungzaehler" value={objektMeta.uebersetzungzaehler} onChange={this.handleEditValueChanged}/>
                  </InputGroup>
                </Col>
                <Col sm={3}>
                  <InputGroup>
                    <InputGroup.Addon>Motor</InputGroup.Addon>
                    <FormControl type="number" step="1" min="1" id="uebersetzungnenner" value={objektMeta.uebersetzungnenner} onChange={this.handleEditValueChanged}/>
                  </InputGroup>
                </Col>
                <Col sm={3}>
                  <FormControl type="number" readOnly={true} tabIndex={-1} value={this.uebersetzungsVerhaeltnis}/>
                </Col>
              </FormGroup>
            </div>}

            {/* Daten der Anzeige */}
            <FormGroup validationState={(objektMeta.anzeigeart === '' || objektMeta.anzeigeaufloesung === '') ? 'error' : undefined}>
              <Col sm={3} componentClass={ControlLabel}>Anzeige</Col>
              {/* <Col sm={1} componentClass={ControlLabel}>Art</Col> */}
              <Col sm={4}>
                <InputGroup>
                  <InputGroup.Addon>Art</InputGroup.Addon>
                  {/* <FormControl type="text" id="anzeigeart" value={this.objektMeta.anzeigeart} onChange={this.handleEditValueChanged}/> */}
                  <FormControl componentClass="select" placeholder="select" id="anzeigeart" value={objektMeta.anzeigeart} onChange={this.handleEditValueChanged}>
                    <option value="">Bitte auswählen...</option>
                    <option value="analog">Analog</option>
                    <option value="digital">Digital</option>
                  </FormControl>
                </InputGroup>
              </Col>
              {/* <Col sm={2} componentClass={ControlLabel} >Auflösung</Col> */}
              <Col sm={4}>
                <InputGroup>
                  <InputGroup.Addon>Auflösung</InputGroup.Addon>
                  <FormControl type="text" id="anzeigeaufloesung" value={objektMeta.anzeigeaufloesung} onChange={this.handleEditValueChanged}/>
                  <InputGroup.Addon>N</InputGroup.Addon>
                  </InputGroup>
              </Col>
            </FormGroup>

            <FormGroup validationState={objekt.sernr === '' ? 'error' : undefined} controlId="sernr">
              <Col sm={3} componentClass={ControlLabel}>Seriennr.</Col>
              <Col sm={9}>
                <FormControl type="text" label="Seriennr." id="sernr" value={this.objekt.sernr} onChange={this.handleEditValueChanged}/>
              </Col>
            </FormGroup>

            <FormGroup validationState={objekt.objektid === '' ? 'error' : undefined} controlId="objektid">
              <Col sm={3} componentClass={ControlLabel}>Kennzeichnung</Col>
              <Col sm={9}>
                <FormControl type="text" label="Kennzeichnung" id="objektid" value={this.objekt.objektid} onChange={this.handleEditValueChanged}/>
              </Col>
            </FormGroup>

            <FormGroup validationState={objektMeta.baujahr === '' ? 'error' : undefined} controlId="baujahr">
              <Col sm={3} componentClass={ControlLabel}>Baujahr</Col>
              <Col sm={9}>
                <FormControl type="text" label="Baujahr" id="baujahr" placeholder="JJJJ" value={this.objektMeta.baujahr} onChange={this.handleEditValueChanged}/>
              </Col>
            </FormGroup>

            <FormGroup validationState={objektMeta.inbetriebnahme === '' ? 'error' : undefined} controlId="inbetriebnahme">
              <Col sm={3} componentClass={ControlLabel}>Inbetriebnahme</Col>
              <Col sm={9}>
                <FormControl type="text" label="Inbetriebnahme" id="inbetriebnahme" placeholder="MM/JJJJ" value={this.objektMeta.inbetriebnahme} onChange={this.handleEditValueChanged}/>
              </Col>
            </FormGroup>

            <FormGroup validationState={objektMeta.fzart === '' ? 'error' : undefined} controlId="fzart">
              <Col componentClass={ControlLabel} sm={3}>Art</Col>
              <Col sm={9}>
                <FormControl componentClass="select" placeholder="select" value={objektMeta.fzart} onChange={this.handleEditValueChanged}>
                    <option value="">Bitte auswählen...</option>
                    <option value="PKW">PKW</option>
                    <option value="LKW">LKW</option>
                    <option value="LKW_PKW">LKW/PKW (kombiniert)</option>
                </FormControl>
              </Col>
            </FormGroup>

            <FormGroup controlId="bemerkung">
              <Col sm={3} componentClass={ControlLabel}>Bemerkung</Col>
              <Col sm={9}>
                <FormControl type="text" label="Bemerkung" id="bemerkung" value={objekt.bemerkung} onChange={this.handleEditValueChanged}/>
              </Col>
            </FormGroup>

          </div>
    );
  }

  render() {
    return (
      <Form horizontal={true} onSubmit={this.handleObjektAnlegenSubmit}>
        <Panel className="panel-header-buttoned"
          header={<ObjektAnlegenHeader caption="Neuen Bremsprüfstand anlegen" showModal={this.showModal}/>}
          footer={(
            <div className="text-right">
              <Button onClick={() => this.uiStore.navigateBack()}>Abbrechen</Button>
              <Button bsStyle="primary" disabled={!this.validate()} type="submit">Anlegen</Button>
            </div>
          )}
        >
          <FormGroup validationState={this.objekt.hersteller === '' ? 'error' : undefined} controlId="hersteller">
            <Col componentClass={ControlLabel} sm={3}>Hersteller</Col>
            <Col sm={9}>
              <FormControl componentClass="select" placeholder="select" value={this.objekt.hersteller} onChange={this.handleEditValueChanged}>
                <option value="">Bitte auswählen...</option>
                {this.objektVorlagenHersteller.map((name) => <option key={name} value={name}>{name}</option>)}
              </FormControl>
            </Col>
          </FormGroup>

          <FormGroup validationState={this.objekt.typbezeichnung === '' ? 'error' : undefined} controlId="typbezeichnung">
            <Col componentClass={ControlLabel} sm={3}>Typ</Col>
            <Col sm={9}>
              <FormControl componentClass="select" placeholder="select" value={this.objekt.typbezeichnung} onChange={this.handleEditValueChanged} readOnly={!this.objekt.hersteller} disabled={!this.objekt.hersteller}>
                <option value="">{this.objekt.hersteller ? 'Bitte auswählen...' : 'Bitte Hersteller auswählen...'}</option>
                {
                  this.objektVorlagenTypen &&
                    this.objektVorlagenTypen.filter(vorlage => vorlage.hersteller === this.objekt.hersteller).map(
                      vorlage => <option key={vorlage.modell} value={vorlage.modell}>{vorlage.modell}</option>)}
                    )
                }
              </FormControl>
            </Col>
          </FormGroup>

          <FormGroup controlId="typzusatz">
            <Col sm={3} componentClass={ControlLabel}>Zusatz</Col>
            <Col sm={9}>
              <FormControl type="text" placeholder="Optionaler Zusatz zum Typ des Prüfstands" label="Zusatz" id="typzusatz" value={this.objektMeta.typzusatz} onChange={this.handleEditValueChanged}/>
            </Col>
          </FormGroup>

          {this.renderEigenschaften()}

          {/* Die Funktion zur Übernahme der ÜO Messmitteldaten wird modal angezeigt */}
          <ObjektMessmittelUebernehmenModal kategorie="bps" onUebernehme={this.uebernehmeMessmittelDaten} show={this.modalVisible} onHideModal={this.hideModal}/>

        </Panel>
      </Form>
    );
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/objekte/ObjektAnlegenBPS.tsx